import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/fetch-api';
import { toast } from '@/hooks/use-toast';
import { invalidateProductionQueries } from '@/utils/queryInvalidation';
import type { OccurrenceComAcoes } from '@/types/occurrences';

/**
 * Retrabalho — ocorrências de um pedido (migration 0075).
 *
 * Passa pela rota dedicada /api/occurrences e NÃO pelo /api/db genérico:
 * `order_occurrences` está em WRITE_BLOCKED_TABLES porque a escrita carrega o
 * gate de gestão, a regra de encerramento e a criação em lote das ações.
 *
 * As AÇÕES são `production_steps` — por isso toda mutação também invalida as
 * queries de produção pelo helper central (`invalidateProductionQueries`), ou o
 * card de retrabalho só apareceria no quadro do setor depois de um F5.
 */

export interface CriarOcorrenciaInput {
  order_id: string;
  titulo: string;
  descricao: string;
  origem_deteccao: string;
  momento: string;
  tipo_problema: string;
  escopo: string;
  pecas_afetadas?: number | null;
  /** Data prometida ao cliente para a entrega do retrabalho (0079). */
  prazo_entrega?: string | null;
  /** Reentrega (0081) — obrigatória em POS_ENTREGA. */
  reentrega_tipo?: string | null;
  reentrega_endereco?: import('@/types/occurrences').ReentregaEndereco | null;
  detectado_por_setor?: string | null;
  feedback_id?: string | null;
}

export interface AcaoInput {
  setor: string;
  data_programada: string;
  acao_descricao?: string | null;
  duracao_prevista_min?: number | null;
  /** Só para setor IMPRESSAO: coluna do quadro por onde a ação entra
   *  (IMPRESSAO_ESTAGIOS_ENTRADA — obrigatório, o servidor recusa sem). */
  impressao_estagio?: string | null;
  /** Grade de peças/partes desta ação (0080) — opcional; grade OU texto. */
  retrabalho_pecas?: import('@/types/occurrences').RetrabalhoPecas | null;
}

/** Erro de ciclo incompleto (HTTP 422) traz a lista do que falta. */
export function faltandoDoErro(error: unknown): string[] {
  const body = (error as { body?: { error?: { faltando?: unknown } } })?.body;
  const faltando = body?.error?.faltando;
  return Array.isArray(faltando) ? faltando.map(String) : [];
}

export const occurrenceKeys = {
  byOrder: (orderId?: string) => ['occurrences', orderId ?? null] as const,
  byStatus: (status: string) => ['occurrences-status', status] as const,
  /** Listagem do painel de retrabalho — prefixo estável para invalidação. */
  list: (desde?: string, ate?: string) => ['occurrences-list', desde ?? null, ate ?? null] as const,
};

export interface OccurrencesListResult {
  ocorrencias: OccurrenceComAcoes[];
  /** true = o LIMIT cortou linhas; a tela avisa em vez de mentir sobre o total. */
  truncado: boolean;
  limite: number;
}

/**
 * Todas as ocorrências do período — fonte do painel de retrabalho.
 *
 * Usa o MESMO endpoint da lista por pedido (só sem order_id): filtro de status,
 * causa, setor e tipo são facetas aplicadas na tela sobre este resultado, o que
 * evita inchar a rota com um parâmetro por coluna e mantém a troca de filtro
 * instantânea (sem ida ao servidor a cada clique).
 */
export function useOccurrencesList(desde?: string, ate?: string, limite = 500) {
  return useQuery({
    queryKey: occurrenceKeys.list(desde, ate),
    queryFn: async (): Promise<OccurrencesListResult> => {
      const qs = new URLSearchParams({ limit: String(limite) });
      if (desde) qs.set('desde', desde);
      if (ate) qs.set('ate', ate);
      const json = await fetchApi<{
        data: OccurrenceComAcoes[];
        truncado?: boolean;
        limite?: number;
      }>(`/api/occurrences?${qs.toString()}`);
      return {
        ocorrencias: json.data ?? [],
        truncado: json.truncado === true,
        limite: json.limite ?? limite,
      };
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Ocorrências RESOLVIDAS aguardando a análise de causa — a fila que o sistema
 * cobra do gerente. Sem ela, o Momento 3 dependeria de alguém lembrar de
 * voltar na ocorrência, que é exatamente o que não acontece na correria.
 */
export function useReworkAguardandoAnalise() {
  return useQuery({
    queryKey: occurrenceKeys.byStatus('RESOLVIDA'),
    queryFn: async () => {
      const json = await fetchApi<{ data: OccurrenceComAcoes[] }>(
        '/api/occurrences?status=RESOLVIDA',
      );
      // "Aguardando análise" = falta causa, OU falta melhoria numa ocorrência
      // que não é reposição (evitavel === false dispensa melhoria — 0076).
      // Mesma régua do fetchReworkSummary; divergência aqui = duas telas mentindo.
      return (json.data ?? []).filter(
        o => !o.causa_etapa ||
          (o.evitavel !== false && !(o.melhoria_descricao ?? '').trim()),
      );
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Retrabalhos ABERTOS — os que ainda não têm nenhuma ação e portanto esperam a
 * produção programar (0079).
 *
 * ABERTA já É "aguardando programação": o trigger `sync_occurrence_status`
 * move a ocorrência para EM_RESOLUCAO no instante em que a primeira ação
 * nasce. Não há filtro novo no servidor nem estado paralelo — o status que já
 * existia responde a pergunta.
 */
export function useReworkAguardandoProgramacao() {
  return useQuery({
    queryKey: occurrenceKeys.byStatus('ABERTA'),
    queryFn: async () => {
      const json = await fetchApi<{ data: OccurrenceComAcoes[] }>(
        '/api/occurrences?status=ABERTA',
      );
      return json.data ?? [];
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

export function useOccurrences(orderId?: string) {
  return useQuery({
    queryKey: occurrenceKeys.byOrder(orderId),
    queryFn: async () => {
      const json = await fetchApi<{ data: OccurrenceComAcoes[] }>(
        `/api/occurrences?order_id=${encodeURIComponent(orderId!)}`,
      );
      return json.data ?? [];
    },
    enabled: !!orderId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Invalida TODA leitura de ocorrência.
 *
 * Exportado porque quem registra e quem precisa ver são pessoas diferentes: o
 * comercial abre o retrabalho, e o aviso tem de acender no quadro da produção,
 * em outro navegador. Por isso o `useProductionCoreSync` chama esta mesma
 * função ao receber o evento de `order_occurrences` — sem uma segunda lista de
 * chaves para manter em sincronia.
 */
export function invalidateOccurrenceQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  orderId?: string,
) {
  queryClient.invalidateQueries({ queryKey: occurrenceKeys.byOrder(orderId) });
  queryClient.invalidateQueries({ queryKey: occurrenceKeys.byStatus('RESOLVIDA') });
  // Fila "aguardando programação": registrar uma ocorrência a enche e criar a
  // primeira ação a esvazia (o trigger move para EM_RESOLUCAO).
  queryClient.invalidateQueries({ queryKey: occurrenceKeys.byStatus('ABERTA') });
  // Painel de retrabalho: prefixo, para pegar qualquer período aberto.
  queryClient.invalidateQueries({ queryKey: ['occurrences-list'] });
}

export function useOccurrenceMutations(orderId?: string) {
  const queryClient = useQueryClient();

  const invalidar = () => {
    invalidateOccurrenceQueries(queryClient, orderId);
    // As ações são production_steps: sem isto o card não aparece no quadro do setor.
    invalidateProductionQueries(queryClient, orderId ? { orderSteps: orderId } : {});
  };

  const erro = (titulo: string) => (error: Error) => {
    const faltando = faltandoDoErro(error);
    toast({
      title: titulo,
      description: faltando.length > 0 ? faltando.join(' · ') : error.message,
      variant: 'destructive',
    });
  };

  const criar = useMutation({
    mutationFn: async (input: CriarOcorrenciaInput) => {
      const json = await fetchApi<{ data: OccurrenceComAcoes }>('/api/occurrences', {
        method: 'POST',
        body: input,
      });
      return json.data;
    },
    onSuccess: () => {
      invalidar();
      toast({ title: '✅ Retrabalho registrado' });
    },
    onError: erro('Não foi possível registrar'),
  });

  const atualizar = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const json = await fetchApi<{ data: OccurrenceComAcoes }>(`/api/occurrences/${id}`, {
        method: 'PATCH',
        body: patch,
      });
      return json.data;
    },
    onSuccess: () => invalidar(),
    onError: erro('Não foi possível salvar'),
  });

  const criarAcoes = useMutation({
    mutationFn: async ({ id, acoes }: { id: string; acoes: AcaoInput[] }) => {
      const json = await fetchApi<{ data: OccurrenceComAcoes }>(`/api/occurrences/${id}/acoes`, {
        method: 'POST',
        body: { acoes },
      });
      return json.data;
    },
    onSuccess: (data, vars) => {
      invalidar();
      const n = vars.acoes.length;
      toast({
        title: n === 1 ? '✅ 1 card criado' : `✅ ${n} cards criados`,
        description: vars.acoes.map(a => a.setor).join(' · '),
      });
    },
    onError: erro('Não foi possível criar as ações'),
  });

  /**
   * Excluir de vez — registro por engano, sem nenhuma ação criada.
   *
   * Separado de `cancelar` de propósito: cancelar preserva a história de algo
   * que aconteceu; excluir apaga o que nunca existiu. O servidor recusa com
   * 409 se a ocorrência já andou, então o botão não precisa adivinhar.
   */
  const excluir = useMutation({
    mutationFn: async (id: string) => {
      await fetchApi(`/api/occurrences/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: () => {
      invalidar();
      toast({ title: '🗑️ Registro de retrabalho excluído' });
    },
    onError: erro('Não foi possível excluir'),
  });

  const encerrar = useMutation({
    mutationFn: async (id: string) => {
      const json = await fetchApi<{ data: OccurrenceComAcoes }>(`/api/occurrences/${id}/encerrar`, {
        method: 'POST',
      });
      return json.data;
    },
    onSuccess: () => {
      invalidar();
      toast({ title: '✅ Ocorrência encerrada', description: 'Causa e melhoria registradas.' });
    },
    onError: erro('Ainda não dá para encerrar'),
  });

  const cancelar = useMutation({
    mutationFn: async ({ id, motivo }: { id: string; motivo: string }) => {
      const json = await fetchApi<{ data: OccurrenceComAcoes }>(`/api/occurrences/${id}/cancelar`, {
        method: 'POST',
        body: { motivo },
      });
      return json.data;
    },
    onSuccess: () => {
      invalidar();
      toast({ title: 'Ocorrência cancelada' });
    },
    onError: erro('Não foi possível cancelar'),
  });

  /**
   * Desfecho sem solução: nenhuma correção atendeu o cliente e o pedido morreu
   * (cancelamento + estorno). Rota própria porque as regras são outras — ações
   * pendentes não bloqueiam e melhoria/alinhamento não são exigidos.
   */
  const encerrarSemSolucao = useMutation({
    mutationFn: async (
      { id, causa_etapa, causa_setor, causa_descricao, motivo }:
      { id: string; causa_etapa: string; causa_setor?: string | null;
        causa_descricao?: string | null; motivo: string },
    ) => {
      const json = await fetchApi<{ data: OccurrenceComAcoes }>(
        `/api/occurrences/${id}/encerrar-sem-solucao`,
        { method: 'POST', body: { causa_etapa, causa_setor, causa_descricao, motivo } },
      );
      return json.data;
    },
    onSuccess: () => {
      invalidar();
      toast({
        title: 'Ocorrência encerrada sem solução',
        description: 'As ações pendentes foram canceladas. Isto não cancela o pedido.',
      });
    },
    onError: erro('Não foi possível encerrar'),
  });

  return { criar, atualizar, criarAcoes, encerrar, cancelar, encerrarSemSolucao, excluir };
}
